import { Node } from 'prosemirror-model';
import { EditorView, NodeView } from 'prosemirror-view';
import { findMultiplierFromDoc } from '../plugins/multiplier-plugin';

function formatNumber(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  if (Number.isInteger(rounded)) return String(rounded);
  const fixed = rounded.toFixed(2);
  return fixed.endsWith('0') ? rounded.toFixed(1) : fixed;
}

export class NumberNodeView implements NodeView {
  dom: HTMLSpanElement;
  private baseInput: HTMLInputElement;
  private computedInput: HTMLInputElement;
  private separator: HTMLSpanElement;
  private node: Node;
  private view: EditorView;
  private getPos: () => number | undefined;
  private editing = false;
  private currentMultiplier = 1;

  constructor(node: Node, view: EditorView, getPos: () => number | undefined) {
    this.node = node;
    this.view = view;
    this.getPos = getPos;
    this.currentMultiplier = findMultiplierFromDoc(view);

    this.dom = document.createElement('span');
    this.dom.className = 'number-node';

    // Base input (hidden when not editing)
    this.baseInput = document.createElement('input');
    this.baseInput.type = 'text';
    this.baseInput.className = 'number-node-base';
    this.dom.appendChild(this.baseInput);

    // Separator (hidden when not editing)
    this.separator = document.createElement('span');
    this.separator.className = 'number-node-separator';
    this.separator.textContent = '→';
    this.dom.appendChild(this.separator);

    // Computed input (always visible)
    this.computedInput = document.createElement('input');
    this.computedInput.type = 'text';
    this.computedInput.className = 'number-node-computed';
    this.dom.appendChild(this.computedInput);

    this.updateDisplay();

    this.baseInput.addEventListener('focus', this.onFocus);
    this.baseInput.addEventListener('blur', this.onBlur);
    this.baseInput.addEventListener('input', this.onBaseInput);
    this.baseInput.addEventListener('keydown', this.onKeyDown);

    this.computedInput.addEventListener('focus', this.onFocus);
    this.computedInput.addEventListener('blur', this.onBlur);
    this.computedInput.addEventListener('input', this.onComputedInput);
    this.computedInput.addEventListener('keydown', this.onKeyDown);
  }

  private onFocus = () => {
    // Don't enter editing mode when editor is locked
    if (!this.view.editable) {
      (document.activeElement as HTMLElement)?.blur();
      return;
    }
    this.editing = true;
    this.dom.classList.add('editing');
    this.updateDisplay();
  };

  private onBlur = (e: FocusEvent) => {
    // Check if focus is moving to the other input
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (relatedTarget === this.baseInput || relatedTarget === this.computedInput) {
      return;
    }
    this.editing = false;
    this.dom.classList.remove('editing');
    this.commitValue();
  };

  private onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.commitValue();
      this.view.focus();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      this.editing = false;
      this.dom.classList.remove('editing');
      this.updateDisplay();
      this.view.focus();
    }
  };

  private onBaseInput = () => {
    const baseValue = parseFloat(this.baseInput.value);
    if (!isNaN(baseValue)) {
      const multiplier = findMultiplierFromDoc(this.view);
      const computed = baseValue * multiplier;
      this.computedInput.value = formatNumber(computed);
      this.updateInputWidths();
    }
  };

  private onComputedInput = () => {
    const computedValue = parseFloat(this.computedInput.value);
    if (!isNaN(computedValue)) {
      const multiplier = findMultiplierFromDoc(this.view);
      const base = computedValue / multiplier;
      this.baseInput.value = formatNumber(base);
      this.updateInputWidths();
    }
  };

  private commitValue() {
    const baseValue = parseFloat(this.baseInput.value);
    if (isNaN(baseValue)) {
      this.updateDisplay();
      return;
    }

    const pos = this.getPos();
    if (pos !== undefined) {
      const tr = this.view.state.tr.setNodeMarkup(pos, null, {
        base: baseValue,
      });
      this.view.dispatch(tr);
    }
  }

  private updateInputWidths() {
    this.baseInput.style.width = `${Math.max(1, this.baseInput.value.length)}ch`;
    this.computedInput.style.width = `${Math.max(1, this.computedInput.value.length)}ch`;
  }

  updateDisplay() {
    const base = this.node.attrs.base;
    const multiplier = findMultiplierFromDoc(this.view);
    const computed = base * multiplier;

    this.baseInput.value = formatNumber(base);
    this.computedInput.value = formatNumber(computed);
    this.updateInputWidths();

    // Disable inputs when editor is not editable (locked mode)
    const editable = this.view.editable;
    this.baseInput.readOnly = !editable;
    this.computedInput.readOnly = !editable;
  }

  update(node: Node): boolean {
    if (node.type !== this.node.type) return false;
    this.node = node;

    // Check if multiplier changed
    const newMultiplier = findMultiplierFromDoc(this.view);
    const multiplierChanged = newMultiplier !== this.currentMultiplier;
    this.currentMultiplier = newMultiplier;

    // Update display if not editing, or if multiplier changed
    if (!this.editing || multiplierChanged) {
      this.updateDisplay();
    }
    return true;
  }

  stopEvent(event: Event): boolean {
    return event.target === this.baseInput || event.target === this.computedInput;
  }

  ignoreMutation(): boolean {
    return true;
  }

  destroy() {
    this.baseInput.removeEventListener('focus', this.onFocus);
    this.baseInput.removeEventListener('blur', this.onBlur);
    this.baseInput.removeEventListener('input', this.onBaseInput);
    this.baseInput.removeEventListener('keydown', this.onKeyDown);

    this.computedInput.removeEventListener('focus', this.onFocus);
    this.computedInput.removeEventListener('blur', this.onBlur);
    this.computedInput.removeEventListener('input', this.onComputedInput);
    this.computedInput.removeEventListener('keydown', this.onKeyDown);
  }
}
